import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../../../services/api';
import './CodingLLMPage.css';

const PROVIDER_TYPE_OPTIONS = [
  { value: 'anthropic', label: 'Anthropic (Claude)' },
  { value: 'openai', label: 'OpenAI (GPT)' },
  { value: 'xai', label: 'xAI (Grok)' },
  { value: 'google', label: 'Google (Gemini)' },
  { value: 'custom_openai', label: 'Custom OpenAI-Compatible' },
];

const PROVIDER_ICONS = {
  anthropic: 'A',
  openai: 'O',
  xai: 'X',
  google: 'G',
  custom_openai: 'C',
};

const PROVIDER_LABELS = {
  anthropic: 'Anthropic',
  openai: 'OpenAI',
  xai: 'xAI',
  google: 'Google',
  custom_openai: 'Custom',
};

const MODEL_SUGGESTIONS = {
  anthropic: ['claude-opus-4-20250918', 'claude-sonnet-4-20250514', 'claude-3-5-sonnet-20241022'],
  openai: ['gpt-4o', 'gpt-4o-mini', 'o1', 'o1-mini'],
  xai: ['grok-3', 'grok-3-mini'],
  google: ['gemini-2.5-pro', 'gemini-2.5-flash'],
  custom_openai: [],
};

const BASE_URL_HINTS = {
  anthropic: 'Leave blank for default Anthropic API.',
  openai: 'Leave blank for default OpenAI API. Set for Azure or proxy.',
  xai: 'Leave blank for default xAI API (https://api.x.ai/v1).',
  google: 'Leave blank for default Gemini API.',
  custom_openai: 'Base URL of your OpenAI-compatible endpoint, e.g. http://host:8000/v1',
};

const EMPTY_FORM = {
  name: '',
  provider_type: 'anthropic',
  model_name: '',
  api_key: '',
  base_url: '',
  max_tokens: 4096,
  temperature: 0.2,
  is_default: false,
  extra_config: {},
};

function ProviderFormModal({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState(() => ({ ...EMPTY_FORM, ...initial }));
  const isEdit = !!initial?.id;

  const set = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const setNumber = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: parseFloat(e.target.value) || 0 }));

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  const suggestions = MODEL_SUGGESTIONS[form.provider_type] || [];

  return (
    <div className="coding-llm-modal-overlay" onClick={onCancel}>
      <div className="coding-llm-modal" onClick={(e) => e.stopPropagation()}>
        <h2>{isEdit ? 'Edit Coding LLM Provider' : 'Add Coding LLM Provider'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Provider Name</label>
            <input type="text" value={form.name} onChange={set('name')} placeholder="e.g. Claude Opus (Production)" required />
          </div>

          <div className="form-group">
            <label>Provider Type</label>
            <select value={form.provider_type} onChange={set('provider_type')}>
              {PROVIDER_TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Model Name</label>
            <input
              type="text"
              value={form.model_name}
              onChange={set('model_name')}
              placeholder={suggestions[0] || 'e.g. claude-opus-4-20250918'}
              required
              list="model-suggestions"
            />
            {suggestions.length > 0 && (
              <datalist id="model-suggestions">
                {suggestions.map((m) => <option key={m} value={m} />)}
              </datalist>
            )}
            <div className="form-hint">The model identifier sent in API calls</div>
          </div>

          <div className="form-group">
            <label>API Key</label>
            <input
              type="password"
              value={form.api_key}
              onChange={set('api_key')}
              placeholder={isEdit ? '(unchanged if left blank)' : 'sk-...'}
            />
          </div>

          <div className="form-group">
            <label>Base URL</label>
            <input type="text" value={form.base_url} onChange={set('base_url')} placeholder="https://..." />
            <div className="form-hint">{BASE_URL_HINTS[form.provider_type]}</div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Max Tokens</label>
              <input type="number" value={form.max_tokens} onChange={setNumber('max_tokens')} min={1} max={200000} />
              <div className="form-hint">Maximum output tokens per response</div>
            </div>
            <div className="form-group">
              <label>Temperature</label>
              <input type="number" value={form.temperature} onChange={setNumber('temperature')} min={0} max={2} step={0.1} />
              <div className="form-hint">0 = deterministic, 1 = creative</div>
            </div>
          </div>

          <div className="form-group">
            <label>
              <input type="checkbox" checked={form.is_default} onChange={set('is_default')} style={{ marginRight: 6 }} />
              Set as default coding LLM
            </label>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={onCancel}>Cancel</button>
            <button type="submit" className="btn-save" disabled={saving || !form.name || !form.model_name}>
              {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CodingLLMPage() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editProvider, setEditProvider] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testResults, setTestResults] = useState({});

  const fetchProviders = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiService.listCodingLLMProviders();
      setProviders(data.providers || []);
      setError(null);
    } catch (err) {
      setError(err?.message || 'Failed to load providers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProviders(); }, [fetchProviders]);

  const handleSave = async (form) => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (editProvider && !payload.api_key) delete payload.api_key;
      if (editProvider) {
        await apiService.updateCodingLLMProvider(editProvider.id, payload);
      } else {
        await apiService.createCodingLLMProvider(payload);
      }
      setShowModal(false);
      setEditProvider(null);
      fetchProviders();
    } catch (err) {
      alert(err?.response?.data?.error || err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete provider "${name}"?`)) return;
    try {
      await apiService.deleteCodingLLMProvider(id);
      fetchProviders();
    } catch (err) {
      alert(err?.message || 'Delete failed');
    }
  };

  const handleSetDefault = async (id) => {
    try {
      await apiService.setDefaultCodingLLMProvider(id);
      fetchProviders();
    } catch (err) {
      alert(err?.message || 'Failed to set default');
    }
  };

  const handleTest = async (id) => {
    setTestResults((prev) => ({ ...prev, [id]: { loading: true } }));
    try {
      const result = await apiService.testCodingLLMProvider(id);
      setTestResults((prev) => ({ ...prev, [id]: result }));
    } catch (err) {
      setTestResults((prev) => ({ ...prev, [id]: { success: false, error: err?.message || 'Test failed' } }));
    }
  };

  return (
    <div className="user-home-layout__page coding-llm-page">
      <div className="user-home-layout__page-header">
        <h1 className="user-home-layout__page-title">Coding LLM</h1>
        <p className="user-home-layout__page-subtitle">
          Configure high-power models (Claude Opus, Grok, GPT) for the Graph AI Agent and code-generation tasks.
        </p>
      </div>

      <div className="user-home-layout__card">
        <div className="providers-toolbar">
          <h2 className="user-home-layout__section-title" style={{ margin: 0 }}>Configured Providers</h2>
          <button className="btn-add-provider" onClick={() => { setEditProvider(null); setShowModal(true); }}>+ Add Provider</button>
        </div>

        {loading && <div className="providers-loading">Loading providers...</div>}
        {error && <p style={{ color: '#ef4444' }}>{error}</p>}

        {!loading && !error && providers.length === 0 && (
          <div className="providers-empty">
            <div style={{ fontSize: 40, marginBottom: 8 }}>{'{ }'}</div>
            <p>No coding LLM providers configured yet.</p>
            <p>Add a provider like Claude Opus or Grok to power the Graph AI Agent.</p>
          </div>
        )}

        {!loading && providers.length > 0 && (
          <div className="providers-grid">
            {providers.map((p) => (
              <div key={p.id} className={`provider-card${p.is_default ? ' provider-card--default' : ''}`}>
                <div className="provider-icon">{PROVIDER_ICONS[p.provider_type] || '?'}</div>
                <div className="provider-info">
                  <div className="provider-header">
                    <h3 className="provider-name">{p.name}</h3>
                    <span className={`provider-badge provider-badge--${p.provider_type}`}>
                      {PROVIDER_LABELS[p.provider_type] || p.provider_type}
                    </span>
                    {p.is_default && <span className="provider-badge provider-badge--default">Default</span>}
                  </div>
                  <div className="provider-meta">
                    <span>Model: <strong>{p.model_name}</strong></span>
                    <span>Max Tokens: <strong>{p.max_tokens}</strong></span>
                    <span>Temp: <strong>{p.temperature}</strong></span>
                    {p.base_url && <span>URL: {p.base_url.length > 40 ? p.base_url.slice(0, 40) + '...' : p.base_url}</span>}
                    <span>Key: {p.api_key_masked || '(none)'}</span>
                  </div>
                  {testResults[p.id] && !testResults[p.id].loading && (
                    <div className={`test-result ${testResults[p.id].success ? 'test-result--success' : 'test-result--error'}`}>
                      {testResults[p.id].success
                        ? `Connected (${testResults[p.id].latency_ms}ms, ${testResults[p.id].tokens_used} tokens)`
                        : `Error: ${testResults[p.id].error}`}
                    </div>
                  )}
                </div>
                <div className="provider-actions">
                  <button className="btn-test" onClick={() => handleTest(p.id)} disabled={testResults[p.id]?.loading}>
                    {testResults[p.id]?.loading ? 'Testing...' : 'Test'}
                  </button>
                  <button onClick={() => { setEditProvider(p); setShowModal(true); }}>Edit</button>
                  {!p.is_default && <button onClick={() => handleSetDefault(p.id)}>Set Default</button>}
                  <button className="btn-delete" onClick={() => handleDelete(p.id, p.name)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <ProviderFormModal
          initial={editProvider ? { ...editProvider, api_key: '' } : null}
          onSave={handleSave}
          onCancel={() => { setShowModal(false); setEditProvider(null); }}
          saving={saving}
        />
      )}
    </div>
  );
}

export default CodingLLMPage;
